import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowUpDown,
  Ban,
  CheckCircle2,
  Copy,
  FileUp,
  Inbox,
  Link2,
  Mail,
  MoreHorizontal,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@staff-app/ui';
import { formatDateMedium as formatDate, formatDayMonth } from '@/lib/format-date';
import {
  trustApi,
  WATERMARKED_KINDS,
  type TrustArtifact,
  type TrustArtifactKind,
  type TrustLog,
  type TrustLogRow,
} from '../lib/trust-api';

const KINDS: TrustArtifactKind[] = [
  'soc2',
  'soc2-bridge',
  'hipaa',
  'tx-ramp',
  'pentest',
  'infosec-policy',
  'vpat',
  'ndaa-889',
  'other',
];

function kindLabel(kind: string): string {
  switch (kind) {
    case 'soc2':
      return 'SOC 2';
    case 'soc2-bridge':
      return 'SOC 2 – Bridge Letter';
    case 'hipaa':
      return 'HIPAA';
    case 'tx-ramp':
      return 'TX-RAMP';
    case 'pentest':
      return 'Pentest';
    case 'infosec-policy':
      return 'InfoSec Policy';
    case 'vpat':
      return 'VPAT';
    case 'ndaa-889':
      return 'NDAA 889';
    default:
      return kind;
  }
}

/** Delivery-log rows per page. */
const LOG_PAGE_SIZE = 25;
/** Pending requests shown before the list folds behind a "show all" toggle. */
const PENDING_COLLAPSED = 10;
const LOG_STATES = ['all', 'active', 'expired', 'revoked'] as const;
type LogState = (typeof LOG_STATES)[number];

function stateFilterLabel(s: LogState): string {
  return s === 'all' ? 'All states' : s.charAt(0).toUpperCase() + s.slice(1);
}

/** Inline union (not an `as const` array) — the headers are explicit, nothing iterates these. */
type LogSortKey = 'createdAt' | 'requester' | 'expiresAt';

/** Matches getLog's server-side `limit` — beyond this, older links aren't fetched at all. */
const LOG_SERVER_CAP = 200;

/** Clickable column header — click to sort, click again to flip direction. */
function SortHeader({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  title,
}: {
  label: string;
  sortKey: LogSortKey;
  activeKey: LogSortKey;
  dir: 'asc' | 'desc';
  onSort: (key: LogSortKey) => void;
  title?: string;
}) {
  const active = activeKey === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      title={title}
      aria-label={`Sort by ${label}${active ? (dir === 'asc' ? ', ascending' : ', descending') : ''}`}
      className="flex cursor-pointer items-center gap-1 font-medium hover:text-foreground"
      data-testid={`trust-log-sort-${sortKey}`}
    >
      {label}
      <ArrowUpDown
        className={active ? 'h-3 w-3 text-foreground' : 'h-3 w-3 text-muted-foreground/50'}
      />
      {active ? <span className="sr-only">{dir === 'asc' ? 'ascending' : 'descending'}</span> : null}
    </button>
  );
}

/**
 * Drops the `@optisigns.com` from a staff address.
 *
 * Every actor in the "Sent by" column is an OptiSigns employee or our own runner, so the
 * domain is the same 15 characters on every row — pure width, zero information. Non-staff
 * addresses (should any appear) are left whole, and the full value stays in the title.
 */
const STAFF_DOMAIN = '@optisigns.com';
function shortActor(email: string): string {
  return email.endsWith(STAFF_DOMAIN) ? email.slice(0, -STAFF_DOMAIN.length) : email;
}

function bytesLabel(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

/**
 * Trust Center — staff control surface for the request→claim SOC 2 flow
 * (claude-dev trust-center 04-build-spec §4, story B3):
 *   • Requests: the queue — every unfulfilled ask, with the requested document and the
 *     use case behind it.
 *   • Delivery log: requests ⋈ links ⋈ redeems (who asked, what was sent, who downloaded).
 *   • Artifacts: upload a new version (becomes the ONLY current of its kind),
 *     flip current back to a retained version.
 * Route-guarded to role ≥ support (root | engineer | support).
 */
/** A pending request handed to the mint dialog to prefill + fulfill. */
export interface FulfillTarget {
  requestId: string;
  name: string;
  email: string;
  /** The requested document kind — preselects the Artifact picker. */
  kind: TrustArtifactKind;
}

/**
 * Tabs, not one scroll.
 *
 * The three jobs on this page run on completely different clocks: the request queue is
 * worked daily, the log is consulted during an audit, and artifacts change a few times a
 * year. Stacked vertically the daily job sat third, below two forms — so `requests` is
 * the landing tab and everything else is one click away.
 */
type TrustTab = 'requests' | 'log' | 'artifacts';

/** Links expiring inside this window are counted separately — they need a re-issue soon. */
const EXPIRING_SOON_DAYS = 7;

/** Header counter. `tone="warning"` is for the count that means someone is waiting. */
function StatTile({
  label,
  value,
  tone = 'default',
  testId,
}: {
  label: string;
  value: number;
  tone?: 'default' | 'warning';
  testId: string;
}) {
  const warn = tone === 'warning' && value > 0;
  return (
    <div
      className={cn(
        'flex min-w-36 flex-col gap-0.5 rounded-md border bg-card px-4 py-3',
        warn && 'border-warning/40 bg-warning-bg',
      )}
      data-testid={testId}
    >
      <span
        className={cn(
          'text-2xl leading-none font-semibold tabular-nums',
          warn ? 'text-warning' : 'text-foreground',
        )}
      >
        {value}
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

export function TrustCenterPage() {
  const [artifacts, setArtifacts] = useState<TrustArtifact[]>([]);
  const [log, setLog] = useState<TrustLog>({ links: [], pendingRequests: [] });
  const [loading, setLoading] = useState(true);
  const [fulfilling, setFulfilling] = useState<FulfillTarget | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [tab, setTab] = useState<TrustTab>('requests');

  const refresh = useCallback(async () => {
    try {
      const [a, l] = await Promise.all([trustApi.listArtifacts(), trustApi.log()]);
      setArtifacts(a.artifacts);
      setLog(l);
    } catch (err) {
      toast.error('Failed to load Trust Center data', {
        description: (err as Error)?.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const pendingCount = log.pendingRequests.length;
  const hasCurrent = artifacts.some((a) => a.current);

  const { activeCount, expiringCount } = useMemo(() => {
    const now = Date.now();
    const cutoff = now + EXPIRING_SOON_DAYS * 24 * 60 * 60 * 1000;
    let active = 0;
    let expiring = 0;
    for (const l of log.links) {
      if (l.revokedAt) continue;
      const exp = new Date(l.expiresAt).getTime();
      if (exp <= now) continue;
      active += 1;
      if (exp <= cutoff) expiring += 1;
    }
    return { activeCount: active, expiringCount: expiring };
  }, [log.links]);

  // A pending row's "Generate link" prefills the dialog; the header button opens it blank.
  const openFulfill = useCallback((target: FulfillTarget) => {
    setFulfilling(target);
    setLinkDialogOpen(true);
  }, []);

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="trust-center-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <ShieldCheck className="h-6 w-6" />
            {'Trust Center'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {'SOC 2 / compliance artifacts, claim links (14-day expiry), and the delivery log. '}
            {'New links always serve the CURRENT artifact version.'}
          </p>
        </div>
        <Button
          onClick={() => {
            setFulfilling(null);
            setLinkDialogOpen(true);
          }}
          className="cursor-pointer"
          data-testid="trust-manual-link-button"
        >
          <Link2 className="mr-2 h-4 w-4" />
          {'Manual link'}
        </Button>
      </div>

      <div className="flex flex-wrap gap-3" data-testid="trust-stats">
        <StatTile
          label="Awaiting fulfillment"
          value={pendingCount}
          tone="warning"
          testId="trust-stat-pending"
        />
        <StatTile label="Active links" value={activeCount} testId="trust-stat-active" />
        <StatTile
          label={`Expiring in ${EXPIRING_SOON_DAYS} days`}
          value={expiringCount}
          testId="trust-stat-expiring"
        />
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as TrustTab)}
        className="flex flex-col gap-4"
      >
        <TabsList className="self-start">
          <TabsTrigger value="requests" className="cursor-pointer" data-testid="trust-tab-requests">
            {pendingCount > 0 ? `Requests (${pendingCount})` : 'Requests'}
          </TabsTrigger>
          <TabsTrigger value="log" className="cursor-pointer" data-testid="trust-tab-log">
            {'Delivery log'}
          </TabsTrigger>
          <TabsTrigger
            value="artifacts"
            className="cursor-pointer"
            data-testid="trust-tab-artifacts"
          >
            {artifacts.length > 0 ? `Artifacts (${artifacts.length})` : 'Artifacts'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          <PendingSection
            pending={log.pendingRequests}
            loading={loading}
            onFulfill={openFulfill}
          />
        </TabsContent>
        <TabsContent value="log">
          <LogSection log={log} loading={loading} onChanged={refresh} />
        </TabsContent>
        <TabsContent value="artifacts">
          <ArtifactsSection artifacts={artifacts} loading={loading} onChanged={refresh} />
        </TabsContent>
      </Tabs>

      <GenerateLinkDialog
        open={linkDialogOpen}
        onOpenChange={(open) => {
          setLinkDialogOpen(open);
          // Drop the prefill on close so the next "Manual link" opens blank rather than
          // silently re-fulfilling the request the operator just handled.
          if (!open) setFulfilling(null);
        }}
        hasCurrent={hasCurrent}
        onMinted={refresh}
        fulfilling={fulfilling}
      />
    </div>
  );
}

function ArtifactsSection({
  artifacts,
  loading,
  onChanged,
}: {
  artifacts: TrustArtifact[];
  loading: boolean;
  onChanged: () => Promise<void>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<TrustArtifactKind>('soc2');
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error('Choose a file first');
      return;
    }
    setUploading(true);
    try {
      const res = await trustApi.uploadArtifact(kind, file);
      toast.success(
        `Uploaded ${res.artifact.filename} — now the current ${kindLabel(kind)} (v${res.artifact.version})`,
      );
      if (fileRef.current) fileRef.current.value = '';
      await onChanged();
    } catch (err) {
      toast.error('Upload failed', { description: (err as Error)?.message });
    } finally {
      setUploading(false);
    }
  };

  const handleSetCurrent = async (artifact: TrustArtifact) => {
    try {
      await trustApi.setCurrent(artifact.id);
      toast.success(`${artifact.filename} (v${artifact.version}) is now current`);
      await onChanged();
    } catch (err) {
      toast.error('Failed to set current', { description: (err as Error)?.message });
    }
  };

  return (
    /* No section heading — the tab trigger already names this panel. */
    <section className="flex flex-col gap-3" data-testid="trust-artifacts-section">

      <div className="flex flex-wrap items-end gap-3 rounded-md border bg-card p-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="trust-upload-kind">{'Kind'}</Label>
          <Select value={kind} onValueChange={(v) => setKind(v as TrustArtifactKind)}>
            <SelectTrigger id="trust-upload-kind" className="w-44" data-testid="trust-upload-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {kindLabel(k)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="trust-upload-file">{'File (pdf)'}</Label>
          <Input
            id="trust-upload-file"
            ref={fileRef}
            type="file"
            accept="application/pdf"
            className="w-72 cursor-pointer"
            data-testid="trust-upload-file"
          />
        </div>
        <Button
          onClick={handleUpload}
          disabled={uploading}
          className="cursor-pointer"
          data-testid="trust-upload-button"
        >
          <FileUp className="mr-2 h-4 w-4" />
          {uploading ? 'Uploading…' : 'Upload as current'}
        </Button>
      </div>

      <Table data-testid="trust-artifacts-table">
        <TableHeader>
          <TableRow>
            <TableHead>{'Kind'}</TableHead>
            <TableHead>{'File'}</TableHead>
            <TableHead>{'Version'}</TableHead>
            <TableHead>{'Size'}</TableHead>
            <TableHead>{'Uploaded'}</TableHead>
            <TableHead>{'Current'}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {artifacts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground">
                {loading ? 'Loading…' : 'No artifacts yet — upload the SOC 2 report above.'}
              </TableCell>
            </TableRow>
          ) : (
            artifacts.map((a) => (
              <TableRow key={a.id} data-testid={`trust-artifact-row-${a.id}`}>
                <TableCell>{kindLabel(a.kind)}</TableCell>
                <TableCell className="font-mono text-xs">{a.filename}</TableCell>
                <TableCell>{`v${a.version}`}</TableCell>
                <TableCell>{bytesLabel(a.sizeBytes)}</TableCell>
                <TableCell>
                  {formatDate(a.uploadedAt)}
                  <span className="ml-1 text-xs text-muted-foreground">{`by ${a.uploadedBy}`}</span>
                </TableCell>
                <TableCell>
                  {a.current ? (
                    <Badge data-testid="trust-current-badge">
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      {'Current'}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">{'Retained'}</span>
                  )}
                </TableCell>
                <TableCell>
                  {!a.current ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="cursor-pointer"
                      onClick={() => handleSetCurrent(a)}
                      data-testid={`trust-set-current-${a.id}`}
                    >
                      {'Make current'}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </section>
  );
}

/**
 * Mint a claim link — a dialog, not a permanent form.
 *
 * Every pending row already carries its own "Generate link" button, so the form was only
 * ever needed for the phone / existing-thread case. Left open on the page it occupied a
 * fixed block of vertical space above the queue and competed with it for attention; behind
 * the header's "Manual link" button it costs nothing until it is wanted.
 */
function GenerateLinkDialog({
  open,
  onOpenChange,
  hasCurrent,
  onMinted,
  fulfilling,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasCurrent: boolean;
  onMinted: () => Promise<void>;
  fulfilling: FulfillTarget | null;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<TrustArtifactKind>('soc2');
  const [minting, setMinting] = useState(false);
  const [minted, setMinted] = useState<{ linkId: string; claimUrl: string } | null>(null);
  const [sending, setSending] = useState(false);

  // Re-seed on every open: prefilled from the pending row being fulfilled, blank for a
  // manual mint. Without the blank branch the previous requester's details would persist
  // into the next dialog and be minted against the wrong person.
  useEffect(() => {
    if (!open) return;
    setName(fulfilling?.name ?? '');
    setEmail(fulfilling?.email ?? '');
    // Preselect the REQUESTED artifact — the operator no longer picks by reading the
    // use-case text; the request itself names the document.
    setKind(fulfilling?.kind ?? 'soc2');
    setMinted(null);
  }, [open, fulfilling]);

  const watermarked = WATERMARKED_KINDS.includes(kind);
  const emailMissing = watermarked && !email.trim();

  const handleMint = async () => {
    setMinting(true);
    try {
      const res = await trustApi.mintLink({
        name: name || undefined,
        email: email || undefined,
        kind,
        // Clears the request from "awaiting fulfillment" — see trust-api.mintLink.
        requestId: fulfilling?.requestId,
      });
      setMinted({ linkId: res.linkId, claimUrl: res.claimUrl });
      toast.success(`Link created — expires ${formatDate(res.expiresAt)}`);
      await onMinted();
    } catch (err) {
      toast.error('Link generation failed', { description: (err as Error)?.message });
    } finally {
      setMinting(false);
    }
  };

  const handleCopy = async () => {
    if (!minted) return;
    await navigator.clipboard.writeText(minted.claimUrl);
    toast.success('Claim URL copied');
  };

  const handleSendEmail = async () => {
    if (!minted) return;
    setSending(true);
    try {
      const res = await trustApi.sendLinkEmail(minted.linkId, {
        claimUrl: minted.claimUrl,
        email: email || undefined,
      });
      toast.success(`Claim link emailed to ${res.to}`);
      await onMinted();
    } catch (err) {
      toast.error('Send failed', { description: (err as Error)?.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="trust-generate-link-dialog">
        <DialogHeader>
          <DialogTitle>{fulfilling ? 'Fulfill request' : 'Generate claim link'}</DialogTitle>
          <DialogDescription>
            {'The link serves the current artifact and expires in 14 days. '}
            {fulfilling
              ? 'The request leaves the queue once the link is generated.'
              : 'Logged as via: manual — for phone / existing-thread cases.'}
          </DialogDescription>
        </DialogHeader>

        {fulfilling ? (
          <div
            className="rounded-md border border-warning/40 bg-warning-bg px-3 py-2 text-sm text-warning"
            data-testid="trust-fulfilling-banner"
          >
            {'Fulfilling the request from '}
            <span className="font-semibold">{fulfilling.name || fulfilling.email}</span>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="trust-link-name">{'Requester name'}</Label>
            <Input
              id="trust-link-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alice Reviewer"
              data-testid="trust-link-name"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="trust-link-email">
              {watermarked ? 'Requester email (required)' : 'Requester email'}
            </Label>
            <Input
              id="trust-link-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alice@company.com"
              data-testid="trust-link-email"
            />
            {watermarked ? (
              <p className="text-xs text-muted-foreground" data-testid="trust-watermark-hint">
                {'This document is watermarked with the requester email and the UTC time the link is generated.'}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="trust-link-kind">{'Artifact'}</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as TrustArtifactKind)}>
              <SelectTrigger id="trust-link-kind" data-testid="trust-link-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {kindLabel(k)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!hasCurrent ? (
          <p className="text-xs text-warning">
            {'No current artifact yet — upload one on the Artifacts tab before generating links.'}
          </p>
        ) : null}

        {minted ? (
          <div className="flex flex-col gap-2">
            <div
              className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2"
              data-testid="trust-claim-url"
            >
              <code className="flex-1 truncate text-xs">{minted.claimUrl}</code>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopy}
                className="cursor-pointer"
                data-testid="trust-copy-button"
              >
                <Copy className="mr-1 h-3 w-3" />
                {'Copy'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSendEmail}
                disabled={sending}
                className="cursor-pointer"
                data-testid="trust-send-email-button"
              >
                <Mail className="mr-1 h-3 w-3" />
                {sending ? 'Sending…' : 'Send email'}
              </Button>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
            data-testid="trust-link-close"
          >
            {minted ? 'Done' : 'Cancel'}
          </Button>
          <Button
            onClick={handleMint}
            disabled={minting || !hasCurrent || emailMissing}
            className="cursor-pointer"
            data-testid="trust-mint-button"
          >
            <Link2 className="mr-2 h-4 w-4" />
            {minting ? 'Generating…' : minted ? 'Generate again' : 'Generate link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The request queue — every ask that hasn't been fulfilled yet.
 *
 * Its own tab rather than a banner nested in the delivery log: this is the one part of the
 * page that represents work outstanding, so it is what the page opens on.
 */
function PendingSection({
  pending,
  loading,
  onFulfill,
}: {
  pending: TrustLog['pendingRequests'];
  loading: boolean;
  onFulfill: (target: FulfillTarget) => void;
}) {
  // The pending list is unbounded (every unfulfilled request, ever) — folding it keeps a
  // long backlog from turning the tab into one endless scroll.
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? pending : pending.slice(0, PENDING_COLLAPSED);

  if (pending.length === 0) {
    return (
      <section
        className="flex flex-col items-center gap-2 rounded-md border bg-card px-6 py-12 text-center"
        data-testid="trust-pending-section"
      >
        <Inbox className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          {loading ? 'Loading…' : 'Nothing awaiting fulfillment'}
        </p>
        {!loading ? (
          <p className="text-xs text-muted-foreground">
            {'New Trust Center requests land here. Past deliveries are on the Delivery log tab.'}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section
      className="overflow-hidden rounded-md border bg-card"
      data-testid="trust-pending-section"
    >
      {/* A table, not a bullet list: use-case text runs to paragraphs and used to
          overflow the row and clip. Rendered in FULL — the reason is what tells the
          operator which artifact to send, so it is the one thing never abbreviated.
          Row height is bounded instead by folding the list past PENDING_COLLAPSED. */}
      <Table data-testid="trust-pending-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-32">{'Requested'}</TableHead>
            <TableHead className="w-72">{'Requester'}</TableHead>
            <TableHead className="w-32">{'Document'}</TableHead>
            <TableHead>{'Use case'}</TableHead>
            <TableHead className="w-40" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.map((r) => (
            <TableRow key={r.id} data-testid={`trust-pending-${r.id}`}>
              <TableCell className="align-top whitespace-nowrap tabular-nums">
                {formatDate(r.createdAt)}
              </TableCell>
              <TableCell className="align-top">
                <div className="font-medium">{r.name || '—'}</div>
                <div className="text-xs whitespace-nowrap text-muted-foreground">{r.email}</div>
              </TableCell>
              <TableCell
                className="align-top font-medium whitespace-nowrap"
                data-testid={`trust-pending-kind-${r.id}`}
              >
                {kindLabel(r.kind)}
              </TableCell>
              <TableCell className="align-top">
                {r.useCase ? (
                  <span className="text-xs whitespace-pre-wrap">{r.useCase}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">{'—'}</span>
                )}
              </TableCell>
              <TableCell className="align-top">
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={() =>
                    onFulfill({
                      requestId: r.id,
                      name: r.name ?? '',
                      email: r.email,
                      kind: (r.kind || 'soc2') as TrustArtifactKind,
                    })
                  }
                  data-testid={`trust-pending-fulfill-${r.id}`}
                >
                  <Link2 className="mr-2 h-4 w-4" />
                  {'Generate link'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pending.length > PENDING_COLLAPSED ? (
        <div className="border-t px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="cursor-pointer"
            onClick={() => setShowAll((v) => !v)}
            data-testid="trust-pending-toggle"
          >
            {showAll ? `Show first ${PENDING_COLLAPSED}` : `Show all ${pending.length} requests`}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function LogSection({
  log,
  loading,
  onChanged,
}: {
  log: TrustLog;
  loading: boolean;
  onChanged: () => Promise<void>;
}) {
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<LogState>('all');
  const [kindFilter, setKindFilter] = useState<TrustArtifactKind | 'all'>('all');
  const linkState = useCallback(
    (l: TrustLogRow): Exclude<LogState, 'all'> =>
      l.revokedAt ? 'revoked' : new Date(l.expiresAt).getTime() <= Date.now() ? 'expired' : 'active',
    [],
  );

  // Date is handled by the Created column sort, not a from–to filter: two date
  // pickers cost more width than they earn when the list is already newest-first
  // and capped at the newest 200.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return log.links.filter((l) => {
      if (stateFilter !== 'all' && linkState(l) !== stateFilter) return false;
      // A link with no artifact row can't match a specific kind.
      if (kindFilter !== 'all' && l.artifact?.kind !== kindFilter) return false;
      if (q) {
        const haystack = `${l.requesterName ?? ''} ${l.requesterEmail ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [log.links, query, stateFilter, kindFilter, linkState]);

  // Server returns createdAt-desc; that stays the default so the view is unchanged
  // until the operator actually sorts.
  const [sortKey, setSortKey] = useState<LogSortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const toggleSort = (key: LogSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'requester' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    // Copy first — filtered derives from props and Array.sort mutates in place.
    return [...filtered].sort((a, b) => {
      if (sortKey === 'requester') {
        const av = (a.requesterName || a.requesterEmail || '').toLowerCase();
        const bv = (b.requesterName || b.requesterEmail || '').toLowerCase();
        return av.localeCompare(bv) * dir;
      }
      const av = new Date(a[sortKey]).getTime();
      const bv = new Date(b[sortKey]).getTime();
      return (av - bv) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const hasFilters = query.trim() !== '' || stateFilter !== 'all' || kindFilter !== 'all';
  const clearFilters = () => {
    setQuery('');
    setStateFilter('all');
    setKindFilter('all');
    setPage(0);
  };

  const pageCount = Math.max(1, Math.ceil(filtered.length / LOG_PAGE_SIZE));
  // Clamp when the list shrinks under us (a revoke + refresh, or a narrowed filter,
  // while sitting on the last page).
  const safePage = Math.min(page, pageCount - 1);
  const pageLinks = sorted.slice(safePage * LOG_PAGE_SIZE, (safePage + 1) * LOG_PAGE_SIZE);
  const handleRevoke = async (linkId: string) => {
    try {
      await trustApi.revokeLink(linkId);
      toast.success('Link revoked');
      await onChanged();
    } catch (err) {
      toast.error('Revoke failed', { description: (err as Error)?.message });
    }
  };

  const now = Date.now();

  return (
    <section className="flex flex-col gap-3" data-testid="trust-log-section">
      <div
        className="flex flex-wrap items-end gap-3 rounded-md border bg-card p-3"
        data-testid="trust-log-filters"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="trust-log-search">{'Requester'}</Label>
          <Input
            id="trust-log-search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
            placeholder="Name or email"
            className="w-56"
            data-testid="trust-log-search"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="trust-log-state">{'State'}</Label>
          <Select
            value={stateFilter}
            onValueChange={(v) => {
              setStateFilter(v as LogState);
              setPage(0);
            }}
          >
            <SelectTrigger id="trust-log-state" className="w-40" data-testid="trust-log-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LOG_STATES.map((s) => (
                <SelectItem key={s} value={s}>
                  {stateFilterLabel(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="trust-log-kind">{'Artifact'}</Label>
          <Select
            value={kindFilter}
            onValueChange={(v) => {
              setKindFilter(v as TrustArtifactKind | 'all');
              setPage(0);
            }}
          >
            <SelectTrigger id="trust-log-kind" className="w-44" data-testid="trust-log-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{'All artifacts'}</SelectItem>
              {/* Same KINDS list the upload picker and mint form use — add a kind
                  once and every surface, including this filter, picks it up. */}
              {KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {kindLabel(k)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="cursor-pointer"
            data-testid="trust-log-clear-filters"
          >
            {'Clear filters'}
          </Button>
        ) : null}
      </div>

      <Table data-testid="trust-log-table">
        <TableHeader>
          <TableRow>
            <TableHead>
              <SortHeader
                label="Created"
                sortKey="createdAt"
                activeKey={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              />
            </TableHead>
            <TableHead>
              <SortHeader
                label="Requester"
                sortKey="requester"
                activeKey={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              />
            </TableHead>
            <TableHead>{'Artifact'}</TableHead>
            <TableHead>{'Sent by'}</TableHead>
            {/* Expiry and download count fold into Status: three columns that each held a
                few characters were what pushed this table into horizontal scroll, where
                Requester and State could never be read at the same time. */}
            <TableHead>
              <SortHeader
                label="Status"
                sortKey="expiresAt"
                activeKey={sortKey}
                dir={sortDir}
                onSort={toggleSort}
                title="Sorts by expiry date"
              />
            </TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground">
                {loading
                  ? 'Loading…'
                  : hasFilters
                    ? 'No links match these filters.'
                    : 'No links yet.'}
              </TableCell>
            </TableRow>
          ) : (
            pageLinks.map((l) => {
              const expired = new Date(l.expiresAt).getTime() <= now;
              const state = l.revokedAt ? 'revoked' : expired ? 'expired' : 'active';
              return (
                <TableRow key={l.id} data-testid={`trust-log-row-${l.id}`}>
                  {/* nowrap + tabular-nums: dates were wrapping to "Aug 13," / "2026",
                      which doubled every row's height and broke the column rhythm. */}
                  <TableCell className="align-top text-xs whitespace-nowrap tabular-nums">
                    {formatDate(l.createdAt)}
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="font-medium whitespace-nowrap">
                      {l.requesterName || '—'}
                    </div>
                    {l.requesterEmail ? (
                      <div
                        className="text-xs whitespace-nowrap text-muted-foreground"
                      >
                        {l.requesterEmail}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-top">
                    {l.artifact ? (
                      <div className="flex flex-col gap-1">
                        {/* Kind first — with >1 artifact type in play, the filename alone
                            doesn't say WHAT was sent at a glance. */}
                        <Badge variant="outline" className="w-fit">
                          {kindLabel(l.artifact.kind)}
                        </Badge>
                        <span
                          className="font-mono text-xs whitespace-nowrap text-muted-foreground"
                          title={`${l.artifact.filename} (v${l.artifact.version})`}
                        >
                          {`${l.artifact.filename} (v${l.artifact.version})`}
                        </span>
                      </div>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex flex-col gap-1">
                      <Badge
                        variant={l.via === 'opticlaw' ? 'secondary' : 'outline'}
                        className="w-fit"
                      >
                        {l.via}
                      </Badge>
                      <span
                        className="text-xs whitespace-nowrap text-muted-foreground"
                        title={l.createdBy}
                      >
                        {shortActor(l.createdBy)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex flex-col gap-1">
                      {state === 'active' ? (
                        // The expiry date stays OUT of the pill: a full date inside a solid
                        // badge made a heavy block that dominated every row.
                        <Badge className="w-fit">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          {'active'}
                        </Badge>
                      ) : state === 'revoked' ? (
                        // Revoked is a deliberate withdrawal of access — it reads as
                        // destructive. Expired is just the clock running out, so it stays
                        // muted. Both used `secondary` before and were indistinguishable.
                        <Badge variant="destructive" className="w-fit">
                          <Ban className="mr-1 h-3 w-3" />
                          {'revoked'}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="w-fit text-muted-foreground">
                          <XCircle className="mr-1 h-3 w-3" />
                          {'expired'}
                        </Badge>
                      )}
                      <span className="text-xs whitespace-nowrap text-muted-foreground tabular-nums">
                        {/* Month + day only — the expiry is always within 14 days of the
                            Created date on the same row, which carries the year. */}
                        {state === 'active' ? `exp ${formatDayMonth(l.expiresAt)}` : null}
                        {state === 'active' && l.redeems.length > 0 ? ' · ' : null}
                        {l.redeems.length > 0 ? (
                          // Count only. One row per redeem, each carrying a full IPv6, was the
                          // widest thing on the page; the date added width without answering
                          // anything the Created column doesn't. Dates + IPs stay in the tooltip.
                          <span
                            className="font-medium text-foreground"
                            title={l.redeems.map((r) => `${formatDate(r.at)} — ${r.ip}`).join('\n')}
                          >
                            {l.redeems.length === 1
                              ? '1 download'
                              : `${l.redeems.length} downloads`}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    {state === 'active' ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            className="cursor-pointer"
                            aria-label={`Actions for ${l.requesterName || l.requesterEmail || 'link'}`}
                            data-testid={`trust-log-actions-${l.id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="cursor-pointer text-destructive focus:text-destructive"
                            onClick={() => handleRevoke(l.id)}
                            data-testid={`trust-revoke-${l.id}`}
                          >
                            {'Revoke link'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {filtered.length > 0 ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"
          data-testid="trust-log-pagination"
        >
          <span>
            {`Showing ${safePage * LOG_PAGE_SIZE + 1}–${safePage * LOG_PAGE_SIZE + pageLinks.length} of ${filtered.length}`}
            {hasFilters ? ` (filtered from ${log.links.length})` : ''}
            {/* getLog caps at 200 server-side with no offset param, so beyond that the
                oldest links are simply absent. Say so rather than implying completeness. */}
            {log.links.length >= LOG_SERVER_CAP ? ' (newest 200 — older links not loaded)' : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
              data-testid="trust-log-prev"
            >
              {'Previous'}
            </Button>
            <span className="tabular-nums">{`Page ${safePage + 1} of ${pageCount}`}</span>
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage(safePage + 1)}
              data-testid="trust-log-next"
            >
              {'Next'}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
